import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button, ConfirmDialog, Toast } from '@/components/ui';

/**
 * Deleting a bot for ever, offered inside the dialog that archives one.
 *
 * Archiving is the right answer almost every time, so it keeps the main
 * button. This sits underneath it, in the one place somebody has just been
 * told what the difference is — two buttons on the row would ask them to know
 * the difference before reading it.
 *
 * The calls survive either way. `calls.bot_id` is ON DELETE SET NULL, so the
 * record of what was said to real people is never collateral; what a purge
 * loses, permanently, is which bot said it.
 */
export default function PurgeBot({ id, name }: { id: string; name: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const preview = useQuery({
    queryKey: ['bot-purge-preview', id],
    queryFn: () =>
      api.get<{ calls: number; assignments: number; kept: string }>(
        `/bots/${id}/purge-preview`,
      ),
    enabled: open,
  });

  const purge = useMutation({
    mutationFn: () => api.post(`/bots/${id}/purge`, { confirm_name: name }),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['bots'] });
      setToast({ message: `${name} deleted permanently.`, tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not delete it'), tone: 'bad' }),
  });

  return (
    <>
      <div className="rounded-md border border-red-200 px-3 py-2 dark:border-red-900">
        <p className="text-xs text-ink-600 dark:text-ink-300">
          Or remove it from the list entirely. The calls stay; they stop saying
          which bot made them.
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="mt-1 text-red-600 dark:text-red-400"
          onClick={() => setOpen(true)}
        >
          Delete for ever
        </Button>
      </div>

      <ConfirmDialog
        open={open}
        title={`Permanently delete ${name}?`}
        confirmLabel="Delete for ever"
        // Typed, and checked on the server too: a confirmation only the
        // browser enforces is one an API client does not have to make.
        requireTyping={name}
        busy={purge.isPending}
        onCancel={() => setOpen(false)}
        onConfirm={() => purge.mutate()}
        body={
          <div className="space-y-2 text-sm">
            <p>
              This cannot be undone. Archiving keeps the bot and takes it off
              the list, which is almost always what you want instead.
            </p>
            {preview.data ? (
              <>
                <p className="text-xs text-ink-500">{preview.data.kept}</p>
                <ul className="text-xs text-ink-600 dark:text-ink-300">
                  <li className="flex justify-between">
                    <span>calls that keep their transcripts</span>
                    <strong className="tabular-nums">
                      {preview.data.calls.toLocaleString()}
                    </strong>
                  </li>
                  <li className="flex justify-between">
                    <span>assignments removed</span>
                    <strong className="tabular-nums">
                      {preview.data.assignments.toLocaleString()}
                    </strong>
                  </li>
                </ul>
              </>
            ) : (
              <p className="text-xs text-ink-500">Counting what would go…</p>
            )}
          </div>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
